import type { ComponentRuntimeDefinition } from '../../catalog/index.ts';
import { z } from 'zod';
import { componentHardMinimum } from '../shared/sizingProfiles.ts';

const lineSeriesConfigSchema = z.object({
  binding: z.string().default('series'),
  xField: z.string().default('time'),
  yField: z.string().default('value'),
  label: z.string().default(''),
  xLabel: z.string().default(''),
  yLabel: z.string().default(''),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#2563eb'),
  tension: z.number().min(0).max(1).default(0.25),
  showPoints: z.boolean().default(false),
  maxPoints: z.number().int().positive().max(20000).default(5000),
  extraSeries: z.array(z.object({
    binding: z.string().default('series'),
    yField: z.string().min(1),
    label: z.string().default(''),
    color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#0891b2'),
    tension: z.number().min(0).max(1).optional(),
  }).strict()).max(10).default([]),
  areaMode: z.boolean().default(true),
  stepLine: z.boolean().default(false),
  smoothReveal: z.boolean().default(true),
  runtimeId: z.string().min(1).optional(),
}).strict();

const histogramConfigSchema = z.object({
  binding: z.string().default('samples'),
  field: z.string().default('value'),
  bins: z.number().int().min(2).max(100).default(12),
  label: z.string().default(''),
  xLabel: z.string().default(''),
  yLabel: z.string().default('频数'),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#0891b2'),
  mode: z.enum(['count', 'frequency']).default('count'),
  maxSamples: z.number().int().positive().max(100000).default(50000),
  autoBins: z.boolean().default(false),
  showMean: z.boolean().default(false),
  showMedian: z.boolean().default(false),
}).strict();

export const lineSeriesDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.line-series@1',
    category: 'chart',
    title: '序列折线图',
    description: '使用 Chart.js 显示同一观察数据中的时间序列。',
    capabilities: ['line-chart', 'time-series', 'selection-source', 'multi-series', 'area-chart'],
    suitableFor: ['趋势', '仿真过程', '参数变化', '多序列比较'],
    inputs: [{
      name: 'series',
      description: '横纵字段组成的序列',
      required: true,
      multiple: false,
      acceptedDatasetKinds: ['series', 'event-series', 'table'],
    }],
    outputs: [],
    events: [{
      type: 'selection.change',
      description: '选择序列数据点',
    }],
    constraints: ['颜色仅接受十六进制颜色值'],
    configSchemaId: 'probfun.line-series.config@1',
    configSchema: {
      type: 'object',
      required: ['binding', 'xField', 'yField'],
      properties: {
        binding: { type: 'string', default: 'series' },
        xField: { type: 'string', default: 'time' },
        yField: { type: 'string', default: 'value' },
        label: { type: 'string', default: '' },
        xLabel: { type: 'string', default: '' },
        yLabel: { type: 'string', default: '' },
        color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$' },
        tension: { type: 'number', minimum: 0, maximum: 1 },
        showPoints: { type: 'boolean', default: false },
        maxPoints: {
          type: 'integer',
          minimum: 1,
          maximum: 20000,
          default: 5000,
        },
        extraSeries: {
          type: 'array',
          maxItems: 10,
          default: [],
          items: {
            type: 'object',
            required: ['yField'],
            properties: {
              binding: { type: 'string', default: 'series' },
              yField: { type: 'string', minLength: 1 },
              label: { type: 'string', default: '' },
              color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$' },
              tension: { type: 'number', minimum: 0, maximum: 1 },
            },
          },
        },
        areaMode: { type: 'boolean', default: true },
        stepLine: { type: 'boolean', default: false },
        smoothReveal: { type: 'boolean', default: true },
        runtimeId: { type: 'string', minLength: 1 },
      },
      additionalProperties: false,
    },
    defaultConfig: {
      binding: 'series',
      xField: 'time',
      yField: 'value',
      label: '',
      xLabel: '',
      yLabel: '',
      color: '#2563eb',
      tension: 0.25,
      showPoints: false,
      maxPoints: 5000,
      extraSeries: [],
      areaMode: true,
      stepLine: false,
      smoothReveal: true,
    },
    examples: [{
      binding: 'series',
      xField: 'time',
      yField: 'queueLength',
      label: '队长',
      xLabel: '时间',
      yLabel: '人数',
      color: '#2563eb',
      tension: 0.2,
      showPoints: false,
      maxPoints: 5000,
      areaMode: true,
      stepLine: true,
      extraSeries: [],
      smoothReveal: true,
      runtimeId: 'queue-runtime',
    }, {
      binding: 'series',
      xField: 'time',
      yField: 'value',
      label: '主序列',
      xLabel: '时间',
      yLabel: '数值',
      color: '#2563eb',
      tension: 0.3,
      showPoints: true,
      maxPoints: 500,
      areaMode: true,
      stepLine: false,
      extraSeries: [{
        binding: 'series',
        yField: 'upperBound',
        label: '上界',
        color: '#ef4444',
      }],
      smoothReveal: true,
    }],
    notes: [
      '线性时间轴从 0 起完整显示曲线，不展示负时间刻度。',
      'smoothReveal 按播放速度匀速揭示已到达数据，减轻离散事件突跳。',
      '单次最多保留 maxPoints 个最近源数据点；支持多序列与面积图。',
    ],
    layout: {
      ...componentHardMinimum('probfun.line-series@1'),
      maxWidth: 1200,
      preferredAspectRatio: 1.6,
    },
    runtimeRequirements: [],
    metadata: {
      layoutHints: {
        roles: ['analysis'],
        preferredSize: 'medium',
        supportsCompact: true,
        supportsExpanded: true,
        canStack: false,
        priority: 50,
      },
    },
  },
  configSchema: lineSeriesConfigSchema,
  validateBlock: (block) => {
    const config = lineSeriesConfigSchema.parse(block.config);
    const binding = block.bindings.find(item => item.input === config.binding);
    if (!binding) {
      return [{
        level: 'error',
        path: 'bindings',
        message: `折线图缺少输入绑定：${config.binding}`,
      }];
    }
    const fields = new Set(
      binding.fields.length > 0
        ? binding.fields
        : binding.field ? [binding.field] : [],
    );
    const missing = [config.xField, config.yField].filter(
      field => !fields.has(field),
    );
    return missing.length > 0
      ? [{
          level: 'error',
          path: 'bindings',
          message: `折线图绑定缺少字段：${missing.join('、')}`,
        }]
      : [];
  },
  loader: () => import('./LineSeriesBlock.vue').then(module => module.default),
};

export const histogramDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.histogram@1',
    category: 'chart',
    title: '直方图',
    description: '对数值字段分箱，并将每个柱对应回原始数据行。',
    capabilities: ['histogram', 'distribution', 'selection-source', 'statistical-markers'],
    suitableFor: ['样本分布', '等待时间', '误差分布', '数据分析'],
    inputs: [{
      name: 'samples',
      description: '需要分箱的样本数据',
      required: true,
      multiple: false,
      acceptedDatasetKinds: ['distribution', 'table', 'series'],
    }],
    outputs: [],
    events: [{
      type: 'selection.change',
      description: '选择分箱内的数据行',
    }],
    constraints: ['分箱数量为 2 至 100'],
    configSchemaId: 'probfun.histogram.config@1',
    configSchema: {
      type: 'object',
      required: ['binding', 'field', 'bins'],
      properties: {
        binding: { type: 'string', default: 'samples' },
        field: { type: 'string', default: 'value' },
        bins: { type: 'integer', minimum: 2, maximum: 100 },
        label: { type: 'string', default: '' },
        xLabel: { type: 'string', default: '' },
        yLabel: { type: 'string', default: '频数' },
        color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$' },
        mode: {
          type: 'string',
          enum: ['count', 'frequency'],
          default: 'count',
        },
        maxSamples: {
          type: 'integer',
          minimum: 1,
          maximum: 100000,
          default: 50000,
        },
      },
      additionalProperties: false,
    },
    defaultConfig: {
      binding: 'samples',
      field: 'value',
      bins: 12,
      label: '',
      xLabel: '',
      yLabel: '频数',
      color: '#0891b2',
      mode: 'count',
      maxSamples: 50000,
      autoBins: false,
      showMean: false,
      showMedian: false,
    },
    examples: [{
      binding: 'samples',
      field: 'waitingTime',
      bins: 12,
      label: '等待时间',
      xLabel: '秒',
      yLabel: '人数',
      color: '#0891b2',
      mode: 'count',
      maxSamples: 50000,
      autoBins: false,
      showMean: false,
      showMedian: false,
    }, {
      binding: 'samples',
      field: 'value',
      bins: 20,
      label: '分布',
      xLabel: '数值',
      yLabel: '频率',
      color: '#0891b2',
      mode: 'frequency',
      maxSamples: 50000,
      autoBins: true,
      showMean: true,
      showMedian: true,
    }],
    notes: ['单次分箱最多消费 maxSamples 个最近样本。', 'autoBins 使用 Sturges 公式自动计算分箱数。', 'showMean/showMedian 在图表上绘制参考线。'],
    layout: {
      ...componentHardMinimum('probfun.histogram@1'),
      maxWidth: 1200,
      preferredAspectRatio: 1.6,
    },
    runtimeRequirements: [],
    metadata: {
      layoutHints: {
        roles: ['analysis'],
        preferredSize: 'medium',
        supportsCompact: true,
        supportsExpanded: true,
        canStack: false,
        priority: 50,
      },
    },
  },
  configSchema: histogramConfigSchema,
  validateBlock: (block) => {
    const config = histogramConfigSchema.parse(block.config);
    const binding = block.bindings.find(item => item.input === config.binding);
    if (!binding) {
      return [{
        level: 'error',
        path: 'bindings',
        message: `直方图缺少输入绑定：${config.binding}`,
      }];
    }
    const fields = binding.fields.length > 0
      ? binding.fields
      : binding.field ? [binding.field] : [];
    return fields.includes(config.field)
      ? []
      : [{
          level: 'error',
          path: 'bindings',
          message: `直方图绑定缺少字段：${config.field}`,
        }];
  },
  loader: () => import('./HistogramBlock.vue').then(module => module.default),
};

// bar
const barConfigSchema = z.object({
  binding: z.string().default('data'),
  categoryField: z.string().default('category'),
  valueField: z.string().default('value'),
  label: z.string().default(''),
  xLabel: z.string().default(''),
  yLabel: z.string().default(''),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#2563eb'),
  orientation: z.enum(['vertical', 'horizontal']).default('vertical'),
  extraSeries: z.array(z.object({
    binding: z.string().default('data'),
    valueField: z.string().min(1),
    label: z.string().default(''),
    color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#0891b2'),
  }).strict()).max(10).default([]),
  exampleData: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]))).max(1000).default([]),
}).strict();

export const barChartDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.bar@1',
    category: 'chart',
    title: '柱状图',
    description: '显示离散类别的频数或概率质量函数（PMF）。',
    capabilities: ['bar-chart', 'categorical', 'selection-source'],
    suitableFor: ['离散类别', '频数对比', 'PMF'],
    inputs: [{ name: 'data', description: '类别和数值数据', required: false, multiple: false, acceptedDatasetKinds: ['table', 'distribution', 'series'] }],
    outputs: [],
    events: [{ type: 'selection.change', description: '选择柱对应的数据行' }],
    constraints: ['x 轴为离散类别名'],
    configSchemaId: 'probfun.bar.config@1',
    configSchema: {
      type: 'object',
      required: ['binding', 'categoryField', 'valueField'],
      properties: {
        binding: { type: 'string', default: 'data' },
        categoryField: { type: 'string' },
        valueField: { type: 'string' },
        label: { type: 'string' },
        xLabel: { type: 'string' },
        yLabel: { type: 'string' },
        color: { type: 'string', pattern: '^#[0-9a-fA-F]{6}$' },
        orientation: { type: 'string', enum: ['vertical', 'horizontal'], default: 'vertical' },
        extraSeries: { type: 'array', maxItems: 10, items: { type: 'object', required: ['valueField'], properties: { binding: { type: 'string' }, valueField: { type: 'string' }, label: { type: 'string' }, color: { type: 'string' } } } },
        exampleData: { type: 'array', maxItems: 1000, items: { type: 'object' } },
      },
      additionalProperties: false,
    },
    defaultConfig: { binding: 'data', categoryField: 'label', valueField: 'value', label: '', xLabel: '', yLabel: '', color: '#2563eb', orientation: 'vertical', extraSeries: [], exampleData: [] },
    examples: [
      { binding: 'data', categoryField: 'face', valueField: 'count', label: '骰子频率', xLabel: '点数', yLabel: '次数', color: '#2563eb', orientation: 'vertical' },
      {
        binding: 'data',
        categoryField: '点数',
        valueField: '频数',
        label: '掷骰子 60 次',
        xLabel: '点数',
        yLabel: '频数',
        color: '#2563eb',
        orientation: 'vertical',
        exampleData: [
          { 点数: '1', 频数: 9 },
          { 点数: '2', 频数: 11 },
          { 点数: '3', 频数: 8 },
          { 点数: '4', 频数: 12 },
          { 点数: '5', 频数: 10 },
          { 点数: '6', 频数: 10 },
        ],
      },
    ],
    notes: ['与 histogram 的区别：bar 适合离散类别，histogram 适合连续分箱。', '无绑定时回退显示 exampleData。'],
    layout: {
      ...componentHardMinimum('probfun.bar@1'),
      maxWidth: 1200,
      preferredAspectRatio: 1.4,
    },
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['analysis'], preferredSize: 'medium', supportsCompact: true, supportsExpanded: true, canStack: false, priority: 50 } },
  },
  configSchema: barConfigSchema,
  validateBlock: (block) => {
    // 允许无绑定，渲染空数据态
    const c = barConfigSchema.safeParse(block.config);
    if (!c.success) {
      return [{ level: 'error' as const, path: 'config', message: '柱状图配置无效' }];
    }
    return [];
  },
  loader: () => import('./BarChartBlock.vue').then(m => m.default),
};

// scatter
const scatterConfigSchema = z.object({
  binding: z.string().default('data'),
  xField: z.string().default('x'),
  yField: z.string().default('y'),
  label: z.string().default(''),
  xLabel: z.string().default(''),
  yLabel: z.string().default(''),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#2563eb'),
  maxPoints: z.number().int().positive().max(50000).default(5000),
  pointRadius: z.number().min(1).max(10).default(3),
  exampleData: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]))).max(1000).default([]),
}).strict();

export const scatterChartDefinition: ComponentRuntimeDefinition = {
  descriptor: {
    key: 'probfun.scatter@1',
    category: 'chart',
    title: '散点图',
    description: '显示二维数据点分布，适合蒙特卡洛抽样和分布可视化。',
    capabilities: ['scatter-chart', 'distribution-2d', 'selection-source'],
    suitableFor: ['蒙特卡洛', '二维分布', '抽样点'],
    inputs: [{ name: 'data', description: 'x/y 字段数据', required: false, multiple: false, acceptedDatasetKinds: ['series', 'table', 'spatial'] }],
    outputs: [],
    events: [{ type: 'selection.change', description: '选择点对应的数据行' }],
    constraints: ['超过 maxPoints 时自动下采样'],
    configSchemaId: 'probfun.scatter.config@1',
    configSchema: {
      type: 'object',
      required: ['binding', 'xField', 'yField'],
      properties: {
        binding: { type: 'string' },
        xField: { type: 'string' },
        yField: { type: 'string' },
        label: { type: 'string' },
        xLabel: { type: 'string' },
        yLabel: { type: 'string' },
        color: { type: 'string' },
        maxPoints: { type: 'integer' },
        pointRadius: { type: 'number', minimum: 1, maximum: 10 },
        exampleData: { type: 'array', maxItems: 1000, items: { type: 'object' } },
      },
      additionalProperties: false,
    },
    defaultConfig: { binding: 'data', xField: 'x', yField: 'y', label: '', xLabel: '', yLabel: '', color: '#2563eb', maxPoints: 5000, pointRadius: 3, exampleData: [] },
    examples: [
      { binding: 'data', xField: 'x', yField: 'y', label: '蒙特卡洛点', xLabel: 'X', yLabel: 'Y', color: '#2563eb', maxPoints: 5000, pointRadius: 3 },
      {
        binding: 'data',
        xField: 'x',
        yField: 'y',
        label: '二维正态抽样',
        xLabel: 'X',
        yLabel: 'Y',
        color: '#2563eb',
        maxPoints: 5000,
        pointRadius: 3,
        exampleData: [
          { x: 0.2, y: 0.4 },
          { x: 1.1, y: 0.8 },
          { x: 0.8, y: 1.3 },
          { x: 1.5, y: 0.6 },
          { x: 0.4, y: 1.1 },
          { x: 1.8, y: 1.4 },
          { x: 2.1, y: 1.2 },
          { x: 1.2, y: 1.9 },
          { x: 0.9, y: 0.3 },
          { x: 1.6, y: 1.7 },
          { x: 0.3, y: 0.9 },
          { x: 1.4, y: 0.2 },
        ],
      },
    ],
    notes: ['适合二维数据分布观察。', '无绑定时回退显示 exampleData。'],
    layout: {
      ...componentHardMinimum('probfun.scatter@1'),
      maxWidth: 1200,
      preferredAspectRatio: 1,
    },
    runtimeRequirements: [],
    metadata: { layoutHints: { roles: ['analysis'], preferredSize: 'medium', supportsCompact: true, supportsExpanded: true, canStack: false, priority: 50 } },
  },
  configSchema: scatterConfigSchema,
  validateBlock: (block) => {
    // 允许无绑定，渲染空数据态
    const c = scatterConfigSchema.safeParse(block.config);
    if (!c.success) {
      return [{ level: 'error' as const, path: 'config', message: '散点图配置无效' }];
    }
    return [];
  },
  loader: () => import('./ScatterChartBlock.vue').then(m => m.default),
};
